import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type MouseEvent,
  type RefObject,
  type TouchEvent,
} from "react";
import { useAddress } from "@/shared/hooks/useAddress";
import { signatureService } from "@/features/protest/services/signature.services";
import { propertyService } from "@/features/properties/model/propertyServices";
import { tokenService } from "@/services/auth/token.service";

/* -------------------------------------------------------------------------- */
/*                         Internal types & API helpers                        */
/* -------------------------------------------------------------------------- */

type ContinuePropertyType =
  | "residential"
  | "commercial"
  | "land_lot"
  | "land_or_lot"
  | "RESIDENTIAL"
  | "COMMERCIAL"
  | "LAND_LOT"
  | "LAND_OR_LOT"
  | string;

export type PropertyRelationshipSubmission =
  | {
      type: "owner";
    }
  | {
      type: "family";
      relationshipToOwner: string;
      reasonForFiling: string;
      drivingLicenseFrontFile: File | null;
    };

const getPropertyType = (type: string, rawType: string): "residential" | "commercial" => {
  const normalizedType = (type || "").toLowerCase();
  const normalizedRawType = (rawType || "").toLowerCase();

  if (normalizedRawType === "sfr" || normalizedType === "residential") {
    return "residential";
  }
  if (normalizedRawType === "mfr" || normalizedType === "commercial") {
    return "commercial";
  }
  return "commercial";
};

type KycCompleteInput = {
  isCommercial: boolean;
  lastNameVerificationOk: boolean;
  isLastNameVerifyFailed: boolean;
  drivingLicenseFile: File | null;
  formationFile: File | null;
  authorizationFile: File | null;
};

function isKycComplete(input: KycCompleteInput): boolean {
  const {
    isCommercial,
    lastNameVerificationOk,
    isLastNameVerifyFailed,
    drivingLicenseFile,
    formationFile,
    authorizationFile,
  } = input;
  if (isCommercial) {
    return Boolean(formationFile && authorizationFile);
  }
  return lastNameVerificationOk || (isLastNameVerifyFailed && Boolean(drivingLicenseFile));
}

async function verifyOwnerLastName(propertyId: string, lastName: string): Promise<boolean> {
  const normalized = lastName.trim();
  if (!propertyId.trim()) {
    throw new Error("Please select a property address before verifying.");
  }
  if (!normalized) {
    throw new Error("Please enter your last name.");
  }
  const res = await signatureService.verifyLastName({
    property_id: propertyId,
    last_name: normalized,
  });
  return Boolean(res.success && res.data?.is_valid);
}

export type HomesteadSubmissionData = {
  homestead: boolean;
  occupancy: "Moving into this home" | "Already living in this home";
  question: "yes" | "no";
  address: string;
};

type SubmitContinueModalInput = {
  paidPropertyIds: string[];
  selectedPropertyTypes: Record<string, ContinuePropertyType>;
  signature: string;
  propertyRelationships: Record<string, PropertyRelationshipSubmission>;
  isGeneralHomesteads: Record<string, boolean>;
  isOver65s: Record<string, boolean>;
  homesteadSubmissions?: Record<string, HomesteadSubmissionData>;
};

type ValidFamilyMemberSubmission = {
  type: "family";
  relationshipToOwner: string;
  reasonForFiling: string;
  drivingLicenseFrontFile: File;
};

function validateFamilyMemberSubmission(
  propertyRelationship: PropertyRelationshipSubmission
): ValidFamilyMemberSubmission | null {
  if (propertyRelationship.type !== "family") return null;

  if (!propertyRelationship.relationshipToOwner.trim()) {
    throw new Error("Please select your relationship to the owner.");
  }
  if (!propertyRelationship.reasonForFiling.trim()) {
    throw new Error("Please enter the reason for filing on their behalf.");
  }
  if (!propertyRelationship.drivingLicenseFrontFile) {
    throw new Error("Please upload your driving license.");
  }

  return {
    type: "family",
    relationshipToOwner: propertyRelationship.relationshipToOwner,
    reasonForFiling: propertyRelationship.reasonForFiling,
    drivingLicenseFrontFile: propertyRelationship.drivingLicenseFrontFile,
  };
}

async function submitContinueModalFlow(input: SubmitContinueModalInput): Promise<void> {
  const {
    paidPropertyIds,
    selectedPropertyTypes,
    signature,
    propertyRelationships,
    isGeneralHomesteads,
    isOver65s,
    homesteadSubmissions,
  } = input;

  if (!paidPropertyIds || paidPropertyIds.length === 0) {
    throw new Error("Please select a property address before continuing.");
  }
  if (!signature) {
    return;
  }

  const bulkUpdates = [];
  const familySubmissions: Record<string, ValidFamilyMemberSubmission> = {};

  for (const propertyId of paidPropertyIds) {
    const rel = propertyRelationships[propertyId];
    if (!rel) continue;

    if (rel.type === "owner") {
      bulkUpdates.push({
        property_ids: propertyId,
        property_user_type: "owner",
        relationshipToOwner: "",
        reasonForFiling: "",
      });
    } else {
      const familyMemberSubmission = validateFamilyMemberSubmission(rel);
      if (familyMemberSubmission) {
        familySubmissions[propertyId] = familyMemberSubmission;
        bulkUpdates.push({
          property_ids: propertyId,
          property_user_type: "familymember",
          relationshipToOwner: familyMemberSubmission.relationshipToOwner.trim(),
          reasonForFiling: familyMemberSubmission.reasonForFiling.trim(),
        });
      }
    }
  }

  if (bulkUpdates.length > 0) {
    const detailsRes = await propertyService.updateDetailsBulk(
      bulkUpdates as unknown as Parameters<typeof propertyService.updateDetailsBulk>[0]
    );
    const detailsMessage = (detailsRes.message || "").toLowerCase();
    const detailsLooksSuccessful =
      detailsRes.success === true ||
      (detailsRes as unknown as Record<string, unknown>).status === "success" ||
      detailsMessage.includes("success");

    if (detailsRes.success === false || (!detailsRes.success && !detailsLooksSuccessful)) {
      throw new Error(detailsRes.message || "Could not update property relationship details.");
    }

    // Upload KYC for all paid properties that have family member relationships if authenticated
    if (Object.keys(familySubmissions).length > 0 && tokenService.hasToken()) {
      try {
        const uploadPromises = Object.entries(familySubmissions).map(([id, submission]) =>
          signatureService.uploadKYCFile({
            property_id: id,
            document_type: "driving_licence",
            file: submission.drivingLicenseFrontFile,
          })
        );
        const uploadResults = await Promise.all(uploadPromises);
        const failedUpload = uploadResults.find((res) => {
          const msg = (res.message || "").toLowerCase();
          return (
            !res.success &&
            !msg.includes("success") &&
            !res.data?.url &&
            !res.data?.driving_licence_url
          );
        });

        if (failedUpload) {
          console.warn(
            failedUpload.message || "Driving license upload failed for one or more properties."
          );
        }
      } catch (uploadErr) {
        console.warn("KYC upload error:", uploadErr);
      }
    }
  }

  const getResolvedPropertyType = (id: string): string => {
    const strId = String(id ?? "");
    let rawType = selectedPropertyTypes[id] || (strId ? selectedPropertyTypes[strId] : undefined);
    if (!rawType && strId) {
      const matchedKey = Object.keys(selectedPropertyTypes).find(
        (k) =>
          k === strId ||
          k.toLowerCase() === strId.toLowerCase() ||
          k.replace(/^prop_/, "") === strId.replace(/^prop_/, "")
      );
      if (matchedKey) {
        rawType = selectedPropertyTypes[matchedKey];
      }
    }
    if (!rawType && Object.keys(selectedPropertyTypes).length > 0) {
      const uniqueTypes = Array.from(new Set(Object.values(selectedPropertyTypes)));
      if (uniqueTypes.length === 1) {
        rawType = uniqueTypes[0];
      }
    }
    const normalized = (rawType || "RESIDENTIAL").toUpperCase();
    if (normalized === "LAND_LOT") return "LAND_OR_LOT";
    return normalized;
  };

  const homesteadPropertyIds = paidPropertyIds.filter((id) => {
    const propType = getResolvedPropertyType(id);
    const isRes = propType === "RESIDENTIAL";
    const strId = String(id ?? "");
    const isGen = isGeneralHomesteads[id] || (strId ? isGeneralHomesteads[strId] : false);
    const is65 = isOver65s[id] || (strId ? isOver65s[strId] : false);
    return isRes && (isGen || is65);
  });
  if (homesteadPropertyIds.length > 0) {
    try {
      // Attempt homestead signature
      const hsRes = await signatureService.submitHomesteadSignatureBulk({
        property_ids: homesteadPropertyIds,
        signature,
        send_email: false,
      });
      const hsMessage = (hsRes.message || "").toLowerCase();
      const hsLooksSuccessful = hsMessage.includes("success");
      if (!hsRes.success && !hsLooksSuccessful) {
        console.warn(hsRes.message || "Homestead signature submission failed.");
      }
    } catch (err) {
      console.error("Homestead signature submission failed:", err);
    }
  }

  const propertyInfo = paidPropertyIds.map((id) => {
    const strId = String(id ?? "");
    const propType = getResolvedPropertyType(id);
    const hs = homesteadSubmissions?.[id] || (strId ? homesteadSubmissions?.[strId] : undefined);
    const isGeneral = isGeneralHomesteads[id] || (strId ? isGeneralHomesteads[strId] : false);
    const isOver65 = isOver65s[id] || (strId ? isOver65s[strId] : false);

    const categories: string[] = [];
    if (isGeneral) categories.push("General Residence Homestead exemption");
    if (isOver65) categories.push("Person Age 65 or Older (or Surviving Spouse)");
    const categoryString = categories.join(", ") || "General Residence Homestead exemption";

    if (propType === "RESIDENTIAL" && (hs?.homestead || isGeneral || isOver65)) {
      return {
        property_id: id,
        property_type: "RESIDENTIAL",
        homestead: true,
        homestead_data: {
          homestead_exemption_category: categoryString,
          occupancy: hs?.occupancy || "Moving into this home",
          occupancy_question: {
            question: hs?.question || "yes",
            address: hs?.address || "",
          },
        },
      };
    }

    return {
      property_id: id,
      property_type: propType,
    };
  });

  // Attempt main protest signature
  const signRes = await signatureService.submitSignatureBulk({
    completed: false,
    property_info: propertyInfo,
    send_email: false,
    signature,
  });

  const signMessage = (signRes.message || "").toLowerCase();
  const signLooksSuccessful = signMessage.includes("success");
  if (!signRes.success && !signLooksSuccessful) {
    throw new Error(signRes.message || "Signature submission failed.");
  }
}

/* -------------------------------------------------------------------------- */
/*                                    Hook                                    */
/* -------------------------------------------------------------------------- */

export type UseSignatureContinueModalParams = {
  isOpen: boolean;
  selectedPropertyTypes?: Record<string, ContinuePropertyType>;
  propertyRelationships?: Record<string, PropertyRelationshipSubmission>;
  isGeneralHomesteads?: Record<string, boolean>;
  isOver65s?: Record<string, boolean>;
  homesteadSubmissions?: Record<string, HomesteadSubmissionData>;
  paidPropertyIds?: string[];
  onClose: () => void;
  onSubmit?: () => void;
};

export type UseSignatureContinueModalResult = {
  visible: boolean;
  propertyId: string;
  isCommercial: boolean;
  lastName: string;
  setLastName: (value: string) => void;
  isLastNameVerifyFailed: boolean;
  isVerifyingLastName: boolean;
  onVerifyLastName: () => void;
  drivingLicenseFileName: string;
  onDrivingLicenseChange: (event: ChangeEvent<HTMLInputElement>) => void;
  formationFileName: string;
  onFormationChange: (event: ChangeEvent<HTMLInputElement>) => void;
  authorizationFileName: string;
  onAuthorizationChange: (event: ChangeEvent<HTMLInputElement>) => void;
  signatureCanvasRef: RefObject<HTMLCanvasElement>;
  startDrawing: (e: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => void;
  draw: (e: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => void;
  stopDrawing: () => void;
  clearSignature: () => void;
  signature: string | null;
  kycComplete: boolean;
  isSubmitting: boolean;
  isSuccessModalOpen: boolean;
  successRedirectSeconds: number;
  signatureConsentAccepted: boolean;
  setSignatureConsentAccepted: (value: boolean) => void;
  handleSubmit: () => Promise<void>;
  onSuccessContinue: () => void;
  onClose: () => void;
};

export function useSignatureContinueModal({
  isOpen,
  selectedPropertyTypes = {},
  propertyRelationships = {},
  isGeneralHomesteads = {},
  isOver65s = {},
  homesteadSubmissions = {},
  paidPropertyIds = [],
  onClose,
  onSubmit,
}: UseSignatureContinueModalParams): UseSignatureContinueModalResult {
  const { selectedAddress: propertyId } = useAddress();
  const signatureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signature, setSignature] = useState<string | null>(null);
  const [lastName, setLastName] = useState("");
  const [isLastNameVerifyFailed, setIsLastNameVerifyFailed] = useState(false);
  const [lastNameVerificationOk, setLastNameVerificationOk] = useState(false);
  const [isVerifyingLastName, setIsVerifyingLastName] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [successRedirectSeconds, setSuccessRedirectSeconds] = useState(5);
  const [drivingLicenseFileName, setDrivingLicenseFileName] = useState("");
  const [drivingLicenseFile, setDrivingLicenseFile] = useState<File | null>(null);
  const [formationFileName, setFormationFileName] = useState("");
  const [formationFile, setFormationFile] = useState<File | null>(null);
  const [authorizationFileName, setAuthorizationFileName] = useState("");
  const [authorizationFile, setAuthorizationFile] = useState<File | null>(null);
  const [signatureConsentAccepted, setSignatureConsentAccepted] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    setLastName("");
    setIsLastNameVerifyFailed(false);
    setLastNameVerificationOk(false);
    setDrivingLicenseFileName("");
    setDrivingLicenseFile(null);
    setFormationFileName("");
    setFormationFile(null);
    setAuthorizationFileName("");
    setAuthorizationFile(null);
    setSignature(null);
    setSignatureConsentAccepted(false);
    setIsSuccessModalOpen(false);
    setSuccessRedirectSeconds(7);
  }, [isOpen]);

  const redirectToSupportingUpload = useCallback(() => {
    setIsSuccessModalOpen(false);
    onSubmit?.();
    onClose();
  }, [onSubmit, onClose]);

  useEffect(() => {
    if (!isOpen || !isSuccessModalOpen) return;
    if (successRedirectSeconds <= 0) return;

    const intervalId = window.setInterval(() => {
      setSuccessRedirectSeconds((seconds) => (seconds <= 1 ? 0 : seconds - 1));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isOpen, isSuccessModalOpen, successRedirectSeconds]);

  useEffect(() => {
    if (!isOpen || !isSuccessModalOpen) return;

    const timeoutId = window.setTimeout(() => {
      redirectToSupportingUpload();
    }, 7000);

    return () => window.clearTimeout(timeoutId);
  }, [isOpen, isSuccessModalOpen, redirectToSupportingUpload]);

  useEffect(() => {
    if (!isOpen) return;

    const setupCanvas = () => {
      const canvas = signatureCanvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const width = rect.width > 0 ? rect.width : 380;
      const height = rect.height > 0 ? rect.height : 180;
      const devicePixelRatio = window.devicePixelRatio || 1;

      canvas.width = Math.floor(width * devicePixelRatio);
      canvas.height = Math.floor(height * devicePixelRatio);

      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.scale(devicePixelRatio, devicePixelRatio);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = "#00A86B";
    };

    const timer = setTimeout(setupCanvas, 50);
    setupCanvas();

    window.addEventListener("resize", setupCanvas);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", setupCanvas);
    };
  }, [isOpen]);

  const getCoordinates = (e: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();

    let clientX: number;
    let clientY: number;
    if ("touches" in e && e.touches.length > 0) {
      const touch = e.touches.item(0);
      if (!touch) return null;
      clientX = touch.clientX;
      clientY = touch.clientY;
    } else if ("clientX" in e) {
      clientX = e.clientX;
      clientY = e.clientY;
    } else {
      return null;
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  const startDrawing = (e: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const coords = getCoordinates(e);
    if (!coords) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    setIsDrawing(true);
  };

  const draw = (e: MouseEvent<HTMLCanvasElement> | TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const coords = getCoordinates(e);
    if (!coords) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    setSignature(canvas.toDataURL());
  };

  const stopDrawing = () => {
    if (isDrawing) {
      const canvas = signatureCanvasRef.current;
      if (canvas) setSignature(canvas.toDataURL());
    }
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignature(null);
  };

  const visible = isOpen && Object.keys(selectedPropertyTypes).length > 0;
  const isCommercial = Object.values(selectedPropertyTypes).some(
    (type) => getPropertyType(type, type) === "commercial"
  );

  const kycComplete = isKycComplete({
    isCommercial,
    lastNameVerificationOk,
    isLastNameVerifyFailed,
    drivingLicenseFile,
    formationFile,
    authorizationFile,
  });

  const onVerifyLastName = useCallback(() => {
    void (async () => {
      try {
        setIsVerifyingLastName(true);
        const isValid = await verifyOwnerLastName(propertyId || "", lastName);
        setLastNameVerificationOk(isValid);
        setIsLastNameVerifyFailed(!isValid);
      } catch (e) {
        console.error(e);
        window.alert(
          e instanceof Error ? e.message : "Could not verify last name. Please try again."
        );
      } finally {
        setIsVerifyingLastName(false);
      }
    })();
  }, [propertyId, lastName]);

  const onDrivingLicenseChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setDrivingLicenseFile(file ?? null);
    setDrivingLicenseFileName(file?.name || "");
  }, []);

  const onFormationChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setFormationFile(file ?? null);
    setFormationFileName(file?.name || "");
  }, []);

  const onAuthorizationChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setAuthorizationFile(file ?? null);
    setAuthorizationFileName(file?.name || "");
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!signature || Object.keys(selectedPropertyTypes).length === 0 || !signatureConsentAccepted)
      return;

    // Use paidPropertyIds if provided, fallback to the selected address propertyId.
    const propertiesToSubmit =
      paidPropertyIds.length > 0 ? paidPropertyIds : propertyId ? [propertyId] : [];

    setIsSubmitting(true);
    try {
      await submitContinueModalFlow({
        paidPropertyIds: propertiesToSubmit,
        selectedPropertyTypes,
        signature,
        propertyRelationships,
        isGeneralHomesteads,
        isOver65s,
        homesteadSubmissions,
      });
      setSuccessRedirectSeconds(5);
      setIsSuccessModalOpen(true);
    } catch (err) {
      console.error(err);
      window.alert(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    signature,
    selectedPropertyTypes,
    propertyId,
    propertyRelationships,
    isGeneralHomesteads,
    isOver65s,
    homesteadSubmissions,
    signatureConsentAccepted,
    paidPropertyIds,
  ]);

  return {
    visible,
    propertyId: propertyId || "",
    isCommercial,
    lastName,
    setLastName,
    isLastNameVerifyFailed,
    isVerifyingLastName,
    onVerifyLastName,
    drivingLicenseFileName,
    onDrivingLicenseChange,
    formationFileName,
    onFormationChange,
    authorizationFileName,
    onAuthorizationChange,
    signatureCanvasRef,
    startDrawing,
    draw,
    stopDrawing,
    clearSignature,
    signature,
    kycComplete,
    isSubmitting,
    isSuccessModalOpen,
    successRedirectSeconds,
    signatureConsentAccepted,
    setSignatureConsentAccepted,
    handleSubmit,
    onSuccessContinue: redirectToSupportingUpload,
    onClose,
  };
}
